"use client";

import { useCallback, useContext } from "react";
import { FrappeContext, useFrappeGetCall, type AuthCredentials, type FrappeConfig } from "frappe-react-sdk";

/* Session hook in place of the SDK's useFrappeAuth. useFrappeAuth only asks
   Frappe for the current user when it can read a `user_id` cookie, and the
   edge no longer relays that cookie (DAST-F360-002: only the HttpOnly sid
   reaches the browser). This hook always asks through the same-origin
   session route, which answers "Guest" itself when there is no sid. */

// One SWR key so every caller (AuthProvider, sign-in pages) shares the answer.
const CURRENT_USER_SWR_ID = "f360-session-user";

export function useFrappeSession() {
  const { auth } = useContext(FrappeContext) as FrappeConfig;
  const { data, error, isLoading, mutate } = useFrappeGetCall<{ message?: string }>(
    "frappe.auth.get_logged_user",
    undefined,
    CURRENT_USER_SWR_ID,
    { shouldRetryOnError: false, revalidateOnFocus: false },
  );

  const login = useCallback(
    (credentials: AuthCredentials) => auth.loginWithUsernamePassword(credentials).then(async (response) => {
      await mutate();
      return response;
    }),
    [auth, mutate],
  );
  const logout = useCallback(() => auth.logout().then(() => mutate(undefined, { revalidate: false })), [auth, mutate]);
  const updateCurrentUser = useCallback(() => mutate(), [mutate]);

  return { currentUser: data?.message ?? null, isLoading, error, login, logout, updateCurrentUser };
}
